import type { MatchState } from '../match/MatchState';
import { PITCH_GEOMETRY } from '../match/PitchGeometry';
import { clamp } from '../match/Vector2';

export type TeamPhase = 'BUILD_UP' | 'PROGRESSION' | 'FINAL_THIRD' | 'ATTACKING_TRANSITION'
  | 'DEFENSIVE_TRANSITION' | 'DEFENDING';

export interface TeamAIState {
  side: 0 | 1;
  phase: TeamPhase;
  phaseStartedAt: number;
  hasPossession: boolean;
  mentality: number;
  pressing: number;
  tempo: number;
  width: number;
  defensiveLine: number;
  risk: number;
  techniqueAggression: number;
  runnerCount: number;
  presserCount: number;
  markingLimit: number;
}

export const initialTeamAIState = (side: 0 | 1): TeamAIState => ({
  side,
  phase: 'DEFENDING',
  phaseStartedAt: 0,
  hasPossession: false,
  mentality: 0.5,
  pressing: 0.5,
  tempo: 0.5,
  width: 0.5,
  defensiveLine: 0.5,
  risk: 0.5,
  techniqueAggression: 0.5,
  runnerCount: 1,
  presserCount: 1,
  markingLimit: 2,
});

export class TeamAIDirector {
  update(state: MatchState): [TeamAIState, TeamAIState] {
    const next = ([0, 1] as const).map((side) => this.plan(state, side)) as [TeamAIState, TeamAIState];
    state.teamAi = next;
    return next;
  }

  private plan(state: MatchState, side: 0 | 1): TeamAIState {
    const tactics = state.teams[side].tactics;
    const read = (camel: string, snake = camel): number => clamp(Number(tactics[camel] ?? tactics[snake] ?? 0.5), 0, 1);
    const hasPossession = state.possessionTeam === side;
    const tempo = read('tempo');
    const risk = read('risk');
    const pressing = read('pressing');
    const mentality = read('mentality');
    const transitionDuration = state.config.transitionWindowSeconds * (0.75 + tempo * 0.5);
    const transitionActive = Number.isFinite(state.lastPossessionChangeTime)
      && state.time - state.lastPossessionChangeTime <= transitionDuration;
    const ballProgress = PITCH_GEOMETRY.relativeProgress(side, state.ball.position.x);
    const phase: TeamPhase = transitionActive
      ? hasPossession ? 'ATTACKING_TRANSITION' : 'DEFENSIVE_TRANSITION'
      : !hasPossession ? 'DEFENDING'
        : ballProgress < PITCH_GEOMETRY.fieldThirds.defensiveEnd ? 'BUILD_UP'
          : ballProgress < PITCH_GEOMETRY.fieldThirds.finalStart ? 'PROGRESSION' : 'FINAL_THIRD';
    const previous = state.teamAi[side];
    const highRiskExtra = risk >= 0.68 ? 1 : 0;
    const transitionExtra = phase === 'ATTACKING_TRANSITION' && tempo >= 0.5 ? 1 : 0;
    const finalThirdExtra = phase === 'FINAL_THIRD' && mentality >= 0.68 ? 1 : 0;

    return {
      side,
      phase,
      phaseStartedAt: previous.phase === phase ? previous.phaseStartedAt : state.time,
      hasPossession,
      mentality,
      pressing,
      tempo,
      width: read('width'),
      defensiveLine: read('defensiveLine', 'defensive_line'),
      risk,
      techniqueAggression: read('techniqueAggression', 'technique_aggression'),
      // One isolated runner is too easy to mark and leaves the ball carrier
      // with only horizontal or backwards passes. Every attacking phase keeps
      // a depth option and a second movement option; risk/transition decide
      // whether a third player joins, rather than whether anyone moves at all.
      runnerCount: hasPossession ? Math.min(3, 2 + highRiskExtra + transitionExtra + finalThirdExtra) : 0,
      presserCount: hasPossession ? 0 : pressing >= 0.72 ? 2 : 1,
      markingLimit: hasPossession ? 0 : Math.min(3,
        (ballProgress <= 62 ? 2 : 1) + (pressing >= 0.72 || read('defensiveLine', 'defensive_line') >= 0.68 ? 1 : 0)),
    };
  }
}
